import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style/CodSummary.css';

const CodSummary = () => {
  const [summaries, setSummaries] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [ordersCache, setOrdersCache] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  const summariesPerPage = 6;

  // Move these declarations to the top, before useEffect hooks
  const user = auth.currentUser;
  const userId = user?.uid;

  // Cache orders data on component mount to avoid repeated fetches
  useEffect(() => {
    if (!userId) return;

    const cacheOrdersData = async () => {
      try {
        const ordersRef = collection(db, 'users', userId, 'orders');
        const ordersSnapshot = await getDocs(ordersRef);
        
        const ordersMap = new Map();
        ordersSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const docId = doc.id;
          
          // Cache by multiple keys for flexible matching
          ordersMap.set(docId, data); // Document ID
          if (data.orderId) {
            ordersMap.set(data.orderId.toString(), data); // Order ID
          }
          if (data.id) {
            ordersMap.set(data.id.toString(), data); // Alternative ID field
          }
        });
        
        setOrdersCache(ordersMap);
        console.log('Orders cached:', ordersMap.size, 'entries'); // Debug log
      } catch (error) {
        console.error('Error caching orders:', error);
      }
    };

    cacheOrdersData();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const codSummaryRef = collection(db, 'users', userId, 'codSummary');
        const snapshot = await getDocs(codSummaryRef);

        const filtered = [];
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (data.status === 'confirmed') {
            filtered.push({
              id: docSnap.id,
              totalAmount: data.totalAmount,
              lastUpdated: data.lastUpdated?.toDate(),
              lastUpdatedString: data.lastUpdated?.toDate().toLocaleString(),
              orders: data.orders || [],
              status: data.status,
            });
          }
        }

        // Sort by newest remittance date first
        filtered.sort((a, b) => {
          if (!a.lastUpdated && !b.lastUpdated) return 0;
          if (!a.lastUpdated) return 1;
          if (!b.lastUpdated) return -1;
          return b.lastUpdated - a.lastUpdated;
        });

        setSummaries(filtered);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load COD summaries.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const getStoreOrderId = (orderIdToMatch, codDocumentId) => {
    if (!ordersCache) return 'Loading...';
    
    console.log('Looking for store order ID with:', { orderIdToMatch, codDocumentId }); // Debug log
    
    // Try multiple matching strategies in order of preference
    const matchingKeys = [
      codDocumentId,           // First try COD record document ID
      orderIdToMatch,          // Then try the stored order ID
      orderIdToMatch?.toString(), // String version
      codDocumentId?.toString()   // String version of document ID
    ].filter(Boolean); // Remove null/undefined values
    
    for (const key of matchingKeys) {
      if (ordersCache.has(key)) {
        const orderData = ordersCache.get(key);
        const storeOrderId = orderData.Storeorder_id || orderData.storeOrderId || orderData.storeorder_id;
        
        console.log('Found match with key:', key, 'Store Order ID:', storeOrderId); // Debug log
        
        return storeOrderId || 'NA';
      }
    }
    
    console.log('No match found for any key:', matchingKeys); // Debug log
    return 'NA';
  };

  const fetchOrderDetails = async (summaryId, orders) => {
    try {
      const orderData = [];
      
      // Batch fetch all COD records at once for better performance
      const codRecordPromises = orders.map(orderId => 
        getDoc(doc(db, 'users', userId, 'codRecords', orderId))
          .then(snap => ({ orderId, snap }))
      );
      
      const codRecordResults = await Promise.all(codRecordPromises);
      
      for (const { orderId, snap } of codRecordResults) {
        if (snap.exists()) {
          const codData = snap.data();
          console.log('COD record data for', orderId, ':', codData); // Debug log
          
          // Determine which ID to display
          let displayOrderId = orderId; // Use document ID as default
          if (codData.orderId && !codData.orderId.toString().startsWith('26617A')) {
            displayOrderId = codData.orderId;
          }
          
          // Get store order ID using cached data and multiple matching strategies
          const storeOrderId = getStoreOrderId(codData.orderId, orderId);
          
          orderData.push({
            ...codData,
            displayOrderId: displayOrderId,
            Storeorder_id: storeOrderId,
            documentId: orderId,
            codRecordId: orderId // Keep track of COD record document ID
          });
        } else {
          orderData.push({ 
            displayOrderId: orderId, 
            status: 'Not Found', 
            Storeorder_id: 'NA',
            documentId: orderId
          });
        }
      }
      
      console.log('Final order data for summary', summaryId, ':', orderData); // Debug log
      setOrderDetails(prev => ({ ...prev, [summaryId]: orderData }));
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Error fetching order details.');
    }
  };

  const toggleDetails = async (summaryId, orders) => {
    if (expandedRow === summaryId) {
      setExpandedRow(null);
    } else {
      if (!orderDetails[summaryId]) {
        await fetchOrderDetails(summaryId, orders);
      }
      setExpandedRow(summaryId);
    }
  };

  // Filter summaries based on search term
  const filteredSummaries = summaries.filter(summary => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in summary ID
    if (summary.id.toLowerCase().includes(searchLower)) return true;
    
    // Search in order details if they're loaded
    const details = orderDetails[summary.id];
    if (details) {
      return details.some(order => 
        (order.displayOrderId && order.displayOrderId.toString().toLowerCase().includes(searchLower)) ||
        (order.Storeorder_id && order.Storeorder_id.toString().toLowerCase().includes(searchLower))
      );
    }
    
    return false;
  });

  const totalPages = Math.ceil(filteredSummaries.length / summariesPerPage);
  const paginatedSummaries = filteredSummaries.slice(
    (currentPage - 1) * summariesPerPage,
    currentPage * summariesPerPage
  );

  return (
   <div className="flex h-screen bg-gray-50">
  {/* Sidebar */}
  <Sidebar />

  {/* Main content */}
  <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
    <ToastContainer/>
    <Header />

    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <h2>COD Summary</h2>

          {/* Search Bar */}
          <div className="search-container" style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search by Order ID or Store Order ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              style={{
                width: '300px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  marginLeft: '10px',
                  padding: '10px 15px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear
              </button>
            )}
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : filteredSummaries.length === 0 ? (
            <p>{searchTerm ? 'No COD summaries found matching your search.' : 'No confirmed COD summaries found.'}</p>
          ) : (
            <>
              <table className="cod-summary-table">
                <thead>
                  <tr>
                    <th>Delivered Date</th>
                    <th>Total Amount</th>
                    <th>Remittance Date</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSummaries.map(summary => (
                    <React.Fragment key={summary.id}>
                      <tr>
                        <td>{summary.id}</td>
                        <td>₹{summary.totalAmount?.toFixed(2)}</td>
                        <td>{summary.lastUpdatedString || 'NA'}</td>
                        <td>{summary.status}</td>
                        <td>
                          <button onClick={() => toggleDetails(summary.id, summary.orders)}>
                            {expandedRow === summary.id ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expandedRow === summary.id && orderDetails[summary.id] && (
                        <tr>
                          <td colSpan="5">
                            <table className="nested-details-table">
                              <thead>
                                <tr>
                                  <th>Order ID</th>
                                  <th>Store Order ID</th>
                                  <th>Amount</th>
                                  <th>Timestamp</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orderDetails[summary.id].map((order, idx) => (
                                  <tr key={idx}>
                                    <td>{order.displayOrderId || 'NA'}</td>
                                    <td>
                                      {order.Storeorder_id}
                                      {/* Debug info for development */}
                                      {process.env.NODE_ENV === 'development' && order.Storeorder_id === 'NA' && (
                                        <small style={{color: 'orange', display: 'block', fontSize: '10px'}}>
                                          COD Doc: {order.documentId}
                                        </small>
                                      )}
                                    </td>
                                    <td>{order.amount ? `₹${order.amount}` : 'NA'}</td>
                                    <td>
                                      {order.timestamp
                                        ? new Date(order.timestamp.seconds * 1000).toLocaleString()
                                        : 'NA'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={currentPage === i + 1 ? 'active' : ''}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodSummary;